import axios from 'axios';

export const formatLabel = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

/** Money is shown from the exact stored decimal string; never re-derived on the client. */
export const formatMoney = (amount: number | string | null | undefined, currency = 'USD') => {
  if (amount === null || amount === undefined || amount === '') return '—';
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (Number.isNaN(n)) return '—';
  return `${currency} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Show a money field in the viewer's chosen currency. The server sends `display_<field>` (already
 * converted, with `display_currency`); if a rate was unavailable it is null and we fall back to the
 * original amount in its own currency rather than showing nothing or guessing.
 */
type Row = Record<string, unknown>;
const asAmount = (v: unknown): string | number | null => (typeof v === 'string' || typeof v === 'number' ? v : null);
const asText = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);

export const showMoney = (row: object | null | undefined, field: string, currencyKey = 'currency') => {
  if (!row) return '—';
  const r = row as Row;
  const converted = asAmount(r[`display_${field}`]);
  const shown = asText(r.display_currency);
  if (converted !== null && shown) return formatMoney(converted, shown);
  return formatMoney(asAmount(r[field]), asText(r[currencyKey]));
};

/** "USD 500.00" when the original differs from what is displayed, else ''. */
export const originalHint = (row: object | null | undefined, field: string, currencyKey = 'currency') => {
  if (!row) return '';
  const r = row as Row;
  const original = asText(r[currencyKey]);
  const shown = asText(r.display_currency);
  if (!original || !shown || original === shown) return '';
  return formatMoney(asAmount(r[field]), original);
};

/** Same idea for aggregate blocks like `display` / `display_totals`. */
export const showTotal = (block: object | null | undefined, field: string, fallbackGroup?: object) => {
  const b = block as Row | null | undefined;
  if (b && b[field] !== undefined) return formatMoney(asAmount(b[field]), asText(b.currency));
  const f = fallbackGroup as Row | undefined;
  return f ? formatMoney(asAmount(f[field]), asText(f.currency)) : '—';
};

export const categoryLabel = (e: { category: string; category_other?: string | null }) =>
  e.category === 'other' && e.category_other ? `Other: ${e.category_other}` : formatLabel(e.category);

export const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const d = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const today = () => new Date().toISOString().slice(0, 10);

export interface ApiFailure { message: string; fieldErrors: Record<string, string> }

/** Turn an axios/Laravel failure into a message plus per-field errors. */
export const parseApiError = (error: unknown): ApiFailure => {
  if (axios.isAxiosError(error) && error.response?.data) {
    const data = error.response.data as { message?: string; errors?: Record<string, string[] | string> };
    const fieldErrors: Record<string, string> = {};
    Object.entries(data.errors ?? {}).forEach(([k, v]) => { fieldErrors[k] = Array.isArray(v) ? v[0] : String(v); });
    return { message: data.message || 'Request failed', fieldErrors };
  }
  return { message: error instanceof Error ? error.message : 'Something went wrong', fieldErrors: {} };
};

export const isFinanceUser = () => ['admin', 'manager'].includes(localStorage.getItem('userRole') || sessionStorage.getItem('userRole') || '');

export const MISTAKE_REASONS: Record<string, string> = {
  period_not_ended: 'Billed before the period ended',
  before_contract_start: 'Period starts before the contract',
  after_contract_end: 'Period is after the contract ended or was cancelled',
  not_a_billing_period: "Doesn't match the contract's billing calendar",
};

export const isAdminUser = () => (localStorage.getItem('userRole') || sessionStorage.getItem('userRole')) === 'admin';

export interface Paginated<T> {
  success: boolean;
  data: T[];
  pagination: { total: number; current_page: number; per_page: number; total_pages: number };
}
